import React, { useEffect } from "react";
import { EditOutlined } from "@ant-design/icons";
import { Button, Form, Input } from "antd";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";

const { TextArea } = Input;

interface AboutData {
  heading?: string;
  description?: string;
}

interface AboutSectionProps {
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  handleCancel: () => void;
  handleSave: () => void;
  form: any;
  aboutData?: AboutData;
  loading?: boolean;
}

const AboutSection: React.FC<AboutSectionProps> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  form,
  aboutData,
  loading,
}) => {
  useEffect(() => {
    if (aboutData) {
      form.setFieldsValue({
        about: {
          heading: aboutData.heading || "",
          description: aboutData.description || "",
        },
      });
    } else {
      form.resetFields();
    }
  }, [aboutData, form]);

  return (
    <div>
      {!isEditing ? (
        <>
          {/* Desktop View */}

          <div className="rounded-[20px] overflow-hidden hidden sm:block">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left text-sm md:text-base">
                    Heading
                  </th>
                  <th className="py-3 px-4 text-left text-sm md:text-base">
                    Description
                  </th>
                  <th className="py-3 px-4 text-end text-sm md:text-base">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {aboutData ? (
                  <tr className="bg-[#F9F9F9] border-b-2 border-[#e9e8e8]">
                    <td className="py-3 px-4 text-gray-700">
                      {aboutData?.heading || "—"}
                    </td>

                    <td className="py-3 px-4 text-gray-700">
                      {aboutData?.description || "—"}
                    </td>

                    <td className="py-3 px-4 text-primary">
                      <div className="flex justify-end">
                        <EditOutlined
                          onClick={() => setIsEditing(true)}
                          className="cursor-pointer hover:text-blue-600 text-[20px]"
                        />
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr className="bg-[#F9F9F9] border-b-2 border-[#e9e8e8]">
                    <td colSpan={3} className="text-center py-6 text-gray-500">
                      <Button type="dashed" onClick={() => setIsEditing(true)}>
                        Add Data
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile View */}

          <div className="sm:hidden">
            {aboutData ? (
              <div className="bg-[#F9F9F9] border border-gray-200 rounded-lg p-4 shadow-sm space-y-3">
                <div>
                  <span className="font-semibold text-primary block mb-1">
                    Heading
                  </span>
                  <p>{aboutData?.heading || "—"}</p>
                </div>

                <div>
                  <span className="font-semibold text-primary block mb-1">
                    Description
                  </span>
                  <p>{aboutData?.description || "—"}</p>
                </div>

                <div>
                  <span className="font-semibold text-primary block mb-1">
                    Action
                  </span>
                  <EditOutlined
                    onClick={() => setIsEditing(true)}
                    className="cursor-pointer text-[18px]"
                  />
                </div>
              </div>
            ) : (
              <div className="bg-[#F9F9F9] border flex justify-center border-gray-200 rounded-lg p-4 shadow-sm">
                <Button type="dashed" onClick={() => setIsEditing(true)}>
                  Add Data
                </Button>
              </div>
            )}
          </div>
        </>
      ) : (
        /* Edit Mode */

        <div>
          <div className="bg-[#F9F9F9] p-4 sm:p-6 rounded-lg sm:rounded-2xl shadow-sm">
            <Form layout="vertical" form={form}>
              <Form.Item
                label="Heading"
                name={["about", "heading"]}
                rules={[{ required: true, message: "Please enter heading" }]}
              >
                <Input placeholder="Enter heading" />
              </Form.Item>

              <Form.Item
                label="Description"
                name={["about", "description"]}
                rules={[
                  { required: true, message: "Please enter description" },
                ]}
              >
                <TextArea rows={4} placeholder="Enter description" />
              </Form.Item>
            </Form>
          </div>

          <div className="flex justify-start gap-3 mt-6 sticky bottom-5">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>

            <ButtonWrapper onClick={handleCancel}>Cancel</ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutSection;